import { HttpClient } from "@angular/common/http"
import { Injectable } from "@angular/core"
import { BehaviorSubject, first, Observable, shareReplay, tap } from "rxjs"
import { environment } from "src/environments/environment"
import { ToastService } from "./toast.service"
import {
    Application,
    CommissionApplication,
    CreatePositionScore,
    CreateSuperiorMap,
    ModifyObject,
    PdfFileResult,
    PositionScore,
    PositionScores,
    SuperiorMap,
    ReqPreviewContracts,
    ContractGenerate,
    ContractPrefix,
} from "../models"

export interface AdminModifyContractsRequest {
    rentalAgreement: {
        url: string
        base64: string
        fileName: string
        contentType: string
    }
    subrentalAgreement: {
        url: string
        base64: string
        fileName: string
        contentType: string
    }
}

@Injectable({
    providedIn: "root",
})
export class KngService {
    private _apiUrl = environment.apiBaseUrl + environment.endpointRoutes.kng
    private _adminApiUrl = environment.apiBaseUrl + environment.endpointRoutes.kngAdmin
    private _rooms$?: Observable<string[]>;

    constructor(private _http: HttpClient, private _messageService: ToastService) { }

    private _contractError = () => this._messageService.add({
        severity: "error",
        summary: "Greška",
        detail: "Ugovor nije moguće dohvatiti. Provjerite status prijave i podatke o najmodavcu.",
        life: 10000,
    })

    private _toDateOnlyString(date: Date | undefined): string {
        if (!date) {
            return ""
        }

        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, "0")
        const day = String(date.getDate()).padStart(2, "0")
        return `${year}-${month}-${day}`
    }

    Get(): Observable<Application[]> {
        return this._http.get<Application[]>(this._adminApiUrl)
    }

    GetSubmitted(): Observable<Application[]> {
        return this._http.get<Application[]>(this._adminApiUrl + "/submitted")
    }

    GetCommission(): Observable<CommissionApplication[]> {
        return this._http.get<CommissionApplication[]>(this._adminApiUrl + "/commission")
    }

    AdminModify(masterId: string, data: ModifyObject): Observable<any> {
        return this._http.patch(this._adminApiUrl + `/modify/${masterId}`, data)
    }

    Cancel(applicationId: string): Observable<Application> {
        return this._http.post<Application>(this._adminApiUrl + `/cancel/${applicationId}`, {})
    }

    GetFileContent(fileNamme: string): Observable<PdfFileResult> {
        return this._http.get<PdfFileResult>(environment.apiBaseUrl + environment.endpointRoutes.staticFiles + `/kng/${fileNamme}`);
    }

    SetSuperior(createSuperiorMap: CreateSuperiorMap): Observable<SuperiorMap> {
        return this._http.post<SuperiorMap>(`${environment.apiBaseUrl}superiorMaps`, createSuperiorMap)
    }

    GetSuperiorMaps(): Observable<SuperiorMap[]> {
        return this._http.get<SuperiorMap[]>(`${environment.apiBaseUrl}superiorMaps`)
    }

    ModifyForEvaluation(data: ModifyObject, applicationId: string): Observable<Application> {
        return this._http
            .patch<Application>(this._adminApiUrl + `/modify-for-evaluation/${applicationId}`, data)
            .pipe(first())
    }

    GetPositionScores(): Observable<PositionScores> {
        return this._http.get<PositionScores>(`${environment.apiBaseUrl}positionScores`)
    }

    SetPositionScore(createPositionScore: CreatePositionScore): Observable<PositionScore> {
        return this._http.post<PositionScore>(`${environment.apiBaseUrl}positionScores`, createPositionScore)
    }

    ModifyPositionScore(masterId: string, data: ModifyObject): Observable<PositionScore> {
        return this._http.patch<PositionScore>(`${environment.apiBaseUrl}positionScores/${masterId}`, data)
    }

    Approve(applicationId: string): Observable<Application> {
        return this._http.post<Application>(`${this._adminApiUrl}/approve/${applicationId}`, {})
    }

    Reject(applicationId: string): Observable<Application> {
        return this._http.post<Application>(`${this._adminApiUrl}/reject/${applicationId}`, {})
    }

    GetRooms(): Observable<string[]> {
        if (!this._rooms$) {
            this._rooms$ = this._http.get<string[]>(`${this._apiUrl}/rooms`).pipe(
                shareReplay(1)
            )
        }
        return this._rooms$
    }

    PreviewContract(
        applicationId: string,
        prefix: ContractPrefix,
        dates: { startDate: Date | undefined, endDate: Date | undefined }
    ): void {
        const baseUrl = `${this._adminApiUrl}/contract/preview/${applicationId}`
        const params = new URLSearchParams({
            contractPrefix: prefix,
            startDate: this._toDateOnlyString(dates.startDate),
            endDate: this._toDateOnlyString(dates.endDate),
        })

        this._http.get(`${baseUrl}?${params.toString()}`, { responseType: "blob" }).subscribe({
            next: blob => window.open(URL.createObjectURL(blob), "_blank"),
            error: this._contractError,
        })
    }

    DownloadContract(
        applicationId: string,
        prefix: ContractPrefix,
        dates: { startDate: Date | undefined, endDate: Date | undefined }
    ): void {
        const baseUrl = `${this._adminApiUrl}/contract/download/${applicationId}`
        const params = new URLSearchParams({
            contractPrefix: prefix,
            startDate: this._toDateOnlyString(dates.startDate),
            endDate: this._toDateOnlyString(dates.endDate),
        })

        this._http.get(`${baseUrl}?${params.toString()}`, { responseType: "blob" }).subscribe({
            next: blob => {
                const a = document.createElement("a")
                const url = window.URL.createObjectURL(blob)
                a.href = url
                a.download = `${prefix}.docx`
                a.click()
                window.URL.revokeObjectURL(url)
            },
            error: this._contractError,
        })
    }

    GenerateContracts(applicationId: string, body: ContractGenerate): Observable<Application> {
        return this._http.post<Application>(`${this._adminApiUrl}/contract/generate/${applicationId}`, {
            ...body,
            startDate: this._toDateOnlyString(body.startDate),
            endDate: this._toDateOnlyString(body.endDate),
        })
    }

    ConfirmSignedContracts(applicationId: string): Observable<Application> {
        return this._http.post<Application>(`${this._adminApiUrl}/contracts/confirm-signed/${applicationId}`, {})
    }

    AdminModifyContracts(applicationId: string, body: AdminModifyContractsRequest): Observable<Application> {
        return this._http.patch<Application>(`${this._adminApiUrl}/contracts/${applicationId}`, body)
    }
}
